import React, { useEffect, useState } from "react";
import { Box, Typography, keyframes } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

const pulseRing = `
  @keyframes pulseRing {
    0% { transform: scale(0.85); opacity: 0.7; }
    50% { transform: scale(1.1); opacity: 0.3; }
    100% { transform: scale(0.85); opacity: 0.7; }
  }
`;

const floatIcon = `
  @keyframes floatIcon {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-8px); }
  }
`;

const dotBounce = `
  @keyframes dotBounce {
    0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
    40% { transform: scale(1); opacity: 1; }
  }
`;

const fadeSlideIn = `
  @keyframes fadeSlideIn {
    from { opacity: 0; transform: translateY(16px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const styleTag = `${pulseRing}${floatIcon}${dotBounce}${fadeSlideIn}`;

const WaitingOnAdminInGroup = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const primary = theme.palette.primary.main;
  const [dots, setDots] = useState([0, 1, 2]);

  return (
    <>
      <style>{styleTag}</style>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "28px",
          flex: 1,
          minHeight: 0,
          width: "100%",
          background: isDark
            ? `linear-gradient(160deg, ${theme.palette.background.default} 0%, ${alpha(primary, 0.06)} 100%)`
            : `linear-gradient(160deg, #f8f9fa 0%, ${alpha(primary, 0.05)} 100%)`,
          animation: "fadeSlideIn 0.5s ease-out",
        }}
      >
        {/* Pulsing ring + icon */}
        <Box sx={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {/* Outer pulse ring */}
          <Box
            sx={{
              position: "absolute",
              width: 120,
              height: 120,
              borderRadius: "50%",
              background: alpha(primary, 0.12),
              animation: "pulseRing 2.4s ease-in-out infinite",
            }}
          />
          {/* Inner pulse ring */}
          <Box
            sx={{
              position: "absolute",
              width: 90,
              height: 90,
              borderRadius: "50%",
              background: alpha(primary, 0.18),
              animation: "pulseRing 2.4s ease-in-out infinite 0.4s",
            }}
          />
          {/* Icon circle */}
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `linear-gradient(135deg, ${primary}, ${alpha(primary, 0.7)})`,
              boxShadow: `0 8px 24px ${alpha(primary, 0.4)}`,
              animation: "floatIcon 3s ease-in-out infinite",
              zIndex: 1,
            }}
          >
            <MeetingRoomIcon sx={{ color: "#fff", fontSize: 36 }} />
          </Box>
        </Box>

        {/* Text section */}
        <Box sx={{ textAlign: "center", px: 3, maxWidth: 340 }}>
          <Typography
            sx={{
              fontSize: "22px",
              fontWeight: 700,
              color: isDark ? "#fff" : theme.palette.text.primary,
              lineHeight: 1.3,
              mb: "8px",
            }}
          >
            Hang on tight!
          </Typography>
          <Typography
            sx={{
              fontSize: "15px",
              fontWeight: 400,
              color: isDark ? alpha("#fff", 0.55) : alpha("#000", 0.5),
              lineHeight: 1.6,
            }}
          >
            The admin is reviewing your request to join the room.
          </Typography>
        </Box>

        {/* Animated dots */}
        <Box sx={{ display: "flex", gap: "10px", alignItems: "center" }}>
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: primary,
                animation: `dotBounce 1.4s ease-in-out infinite`,
                animationDelay: `${i * 0.16}s`,
              }}
            />
          ))}
        </Box>

        {/* Subtle hint */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            px: "16px",
            py: "8px",
            borderRadius: "20px",
            background: isDark ? alpha("#fff", 0.05) : alpha("#000", 0.04),
            border: `1px solid ${isDark ? alpha("#fff", 0.07) : alpha("#000", 0.07)}`,
          }}
        >
          <AccessTimeIcon
            sx={{
              fontSize: "16px",
              color: isDark ? alpha("#fff", 0.4) : alpha("#000", 0.4),
            }}
          />
          <Typography
            sx={{
              fontSize: "13px",
              color: isDark ? alpha("#fff", 0.4) : alpha("#000", 0.4),
            }}
          >
            This usually takes a few seconds
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default WaitingOnAdminInGroup;
